import { useOrg } from '@/organization/useOrg';
import { useGetSubproject } from '@/queries/useSubprojects';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DecarbFooterBar } from '../components/DecarbFooterBar.tsx';
import { DecarbPageLayout } from '../components/DecarbPageLayout.tsx';
import { DecarbTabsPanel } from '../components/DecarbTabsPanel.tsx';
import { HelperText } from '../components/HelperText.tsx';
import { useDecarbTechSelection } from '../hooks.ts';
import { usePencilsDown } from '../hooks/usePencilsDown';
import { useWaterPumpWithMetrics } from '../queries/waterPump';
import { safelySaveSubproject } from '../saveHelpers.ts';
import { Tab } from '../types';
import { isPassiveTechnology, storedCategoryCapitalCost } from '../utils.ts';
import { calculateEnergyDemand } from '../utils/calculations.ts';
import { WaterPumpSelectionDetails } from './WaterPumpSelectionDetails.tsx';

const TECHNOLOGY_KEY = 'waterpump';

export const WaterPumpSelection: React.FC = () => {
  const queryClient = useQueryClient();
  const { subId } = useParams();
  const { selectedOrg } = useOrg();
  const orgId = selectedOrg?.id ?? '';
  const pencilsDown = usePencilsDown();

  const { data: fetchedSubproject } = useGetSubproject(orgId, subId || '');
  const { data: waterPumps } = useWaterPumpWithMetrics(
    fetchedSubproject?.conventionalAlternative?.buildingParams
      ?.conditionedSpace,
  );

  const {
    costsArePending,
    currentTab,
    selectedItems: selectedWaterPumps,
    availableItems: availableWaterPumps,
    subproject,
    setCurrentTab,
    handleAdd,
    handleRemove,
    handleChangeItemCount,
    copyItemSelectionFromTab,
  } = useDecarbTechSelection(TECHNOLOGY_KEY, fetchedSubproject, waterPumps);

  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialLoadRef = useRef(true);
  const [isSaving, setIsSaving] = useState(false);

  // Reset initial load flag when fetched data changes
  useEffect(() => {
    isInitialLoadRef.current = true;
  }, [fetchedSubproject]);

  // Debounced auto-save after selection changes
  useEffect(() => {
    if (pencilsDown) return;
    if (!subproject || !subId || !selectedOrg) return;

    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      return;
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      setIsSaving(true);
      safelySaveSubproject(subId, selectedOrg.id, subproject, queryClient)
        .catch(console.error)
        .finally(() => setIsSaving(false));
    }, 500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [pencilsDown, subproject, subId, selectedOrg, queryClient]);

  const getCostAndEnergyMetrics = useCallback(
    (tab: Tab) => {
      if (!subproject?.[tab]?.technologyParams?.[TECHNOLOGY_KEY]?.params) {
        return undefined;
      }

      const params = subproject[tab].technologyParams[TECHNOLOGY_KEY].params;

      if (params.length === 0 || !waterPumps || waterPumps.length === 0) {
        return undefined;
      }

      const mappedWaterPumps = params
        .map((param) => {
          const foundItem = waterPumps.find(
            (waterPump) => String(waterPump.id) === param.techId,
          );
          if (!foundItem) return null;
          return { ...foundItem, count: param.units };
        })
        .filter((item) => item !== null);

      // reduce the list of selected water pumps to the sum total of costs, energyDemand and coverage
      const { energyDemand, systemCoverageArea } = mappedWaterPumps.reduce(
        (acc, waterPump) => {
          return {
            energyDemand:
              acc.energyDemand +
              calculateEnergyDemand(waterPump.energyUsePerYr, waterPump.count),
            // Coverage calculation: count / limit gives the fraction covered
            // Passive technologies have 0% coverage
            systemCoverageArea:
              acc.systemCoverageArea +
              (isPassiveTechnology(waterPump)
                ? 0
                : waterPump.count / Math.max(1, waterPump.limit)),
          };
        },
        {
          energyDemand: 0,
          systemCoverageArea: 0,
        },
      );

      // Capital comes from the stored category total, never local arithmetic --
      // see storedCategoryCapitalCost for why.
      const cost = storedCategoryCapitalCost(
        subproject[tab],
        TECHNOLOGY_KEY,
        costsArePending,
      );

      if (!subproject?.[tab].buildingParams.conditionedSpace) {
        return { cost, energyDemand, systemCoveragePercentage: 0 };
      }

      return {
        cost,
        energyDemand,
        systemCoveragePercentage: systemCoverageArea,
      };
    },
    [subproject, waterPumps, costsArePending],
  );

  const conventionalMetrics = useMemo(
    () => getCostAndEnergyMetrics('conventionalAlternative'),
    [getCostAndEnergyMetrics],
  );
  const reducedCarbonMetrics = useMemo(
    () => getCostAndEnergyMetrics('reducedCarbonAlternative'),
    [getCostAndEnergyMetrics],
  );
  const zeroCarbonMetrics = useMemo(
    () => getCostAndEnergyMetrics('zeroCarbonAlternative'),
    [getCostAndEnergyMetrics],
  );

  return (
    <DecarbPageLayout
      title={'SELECT WATERPUMP COMPONENTS'}
      tabs={
        <DecarbTabsPanel
          currentTab={currentTab}
          onClick={setCurrentTab}
          conventionalMetrics={conventionalMetrics}
          reducedCarbonMetrics={reducedCarbonMetrics}
          zeroCarbonMetrics={zeroCarbonMetrics}
        />
      }
      footer={
        <DecarbFooterBar
          currentTab={currentTab}
          changeTab={setCurrentTab}
          saveSubproject={(onSuccessFn: () => void) => {
            if (pencilsDown) {
              onSuccessFn();
              return;
            }
            if (subproject && subId && selectedOrg) {
              safelySaveSubproject(
                subId,
                selectedOrg.id,
                subproject,
                queryClient,
              )
                .then(() => {
                  onSuccessFn();
                })
                .catch((error) => {
                  console.error('Failed to save water pump data:', error);
                  alert('Failed to save changes. Please try again.');
                });
            }
          }}
        />
      }
    >
      <WaterPumpSelectionDetails
        onAdd={handleAdd}
        onRemove={handleRemove}
        onChangeCount={handleChangeItemCount}
        availableWaterPumps={availableWaterPumps}
        selectedWaterPumps={selectedWaterPumps}
        costEnergyAndCoverageMetrics={getCostAndEnergyMetrics(currentTab)}
        isLoading={isSaving}
        readOnly={pencilsDown}
        helperTextComponent={
          pencilsDown ? undefined : (
            <HelperText
              tab={currentTab}
              onCopySelection={() => copyItemSelectionFromTab(currentTab)}
            />
          )
        }
      />
    </DecarbPageLayout>
  );
};
